import React from 'react';

import {zodResolver} from '@hookform/resolvers/zod';
import {useSnackbar} from 'notistack';
import {useForm} from 'react-hook-form';

import {Typography} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import {PasswordStrength} from './PasswordStrength';
import {changePassword as changePasswordRequest} from '../../../shared/api';
import {
  AUTHENTICATION_ERROR,
  ERROR,
  PASSWORD_NOT_EQUAL,
  PASSWORD_SUCCESS,
} from '../../../shared/constants';
import {useAuth} from '../../../shared/contexts';
import {
  PasswordChangeFormValues,
  PasswordChangeProps,
  passwordChangeSchema,
} from '../../../shared/models';

export const PasswordChange: React.FC<PasswordChangeProps> = props => {
  const [requestRunning, setRequestRunning] = React.useState<boolean>(false);
  const {token, updateToken} = useAuth();
  const {enqueueSnackbar} = useSnackbar();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: {errors, isValid},
  } = useForm<PasswordChangeFormValues>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {password: '', passwordNew1: '', passwordNew2: ''},
    mode: 'onChange',
  });

  const passwordNew1 = watch('passwordNew1');

  const changePassword = (values: PasswordChangeFormValues) => {
    setRequestRunning(true);

    changePasswordRequest(
      new Map(Object.entries(values)) as unknown as Map<string, string>,
      token,
    )
      .then(res => {
        enqueueSnackbar(PASSWORD_SUCCESS, {variant: 'success'});
        setRequestRunning(false);
        reset();
        updateToken(res.data);
        props.onClose();
      })
      .catch(err => {
        console.error(err);

        switch (err?.response?.status) {
          case 401:
            enqueueSnackbar(AUTHENTICATION_ERROR, {variant: 'error'});

            break;
          case 417:
            enqueueSnackbar(PASSWORD_NOT_EQUAL, {variant: 'warning'});

            break;
          default:
            enqueueSnackbar(ERROR, {variant: 'error'});
        }
        reset();
        setRequestRunning(false);
      });
  };

  return (
    <Dialog open={props.open} title="Passwort ändern" fullWidth>
      <DialogTitle>Passwort ändern</DialogTitle>
      <DialogContent>
        {props.message && (
          <Typography variant="h3" color="textSecondary">
            {props.message}
          </Typography>
        )}
        <form onSubmit={handleSubmit(changePassword)} id="passwordChangeForm">
          <TextField
            id="password"
            autoComplete="password"
            type="password"
            required
            fullWidth
            label="Aktuelles Passwort"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            margin="normal"
            error={!!errors.password}
            helperText={errors.password?.message}
            {...register('password')}
          />
          <TextField
            id="passwordNew1"
            autoComplete="new-password"
            type="password"
            required
            fullWidth
            label="Neues Passwort"
            margin="normal"
            error={!!errors.passwordNew1}
            helperText={errors.passwordNew1?.message}
            {...register('passwordNew1')}
          />
          <TextField
            id="passwordNew2"
            autoComplete="new-password"
            type="password"
            required
            fullWidth
            label="Neues Passwort (Wiederholung)"
            margin="normal"
            error={!!errors.passwordNew2}
            helperText={errors.passwordNew2?.message}
            {...register('passwordNew2')}
          />
        </form>
        <PasswordStrength password={passwordNew1} />
      </DialogContent>
      <DialogActions>
        <Button
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          disabled={requestRunning || !isValid}
          type="submit"
          form="passwordChangeForm"
        >
          Speichern
        </Button>
        <Button onClick={props.onClose} disabled={requestRunning}>
          {props.closeButtonText ? props.closeButtonText : 'Abbrechen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordChange;
